import { entrySessionOf } from "@cleo/contracts";
import type { useNavigate } from "@tanstack/react-router";

import type { NotificationDestination } from "./notifications";
import {
  enterWorld,
  readWorldRoom,
  useNeucleosWorldStore,
  worldEntryKeepsPage,
} from "./worldStore";
import { readWorldRoomIndex } from "./worldSessionClient";

/**
 * A safety bound only: an owed notice lasts while the person stays in its
 * World, however long the World's landing takes, and is dropped the moment
 * another World (or Local) is active.
 */
export const OWED_NOTICE_WINDOW_MS = 5 * 60_000;
let owed: { readonly worldKey: string; readonly notice: string; readonly at: number } | null = null;

/**
 * W-a67b0503: the notice opened into a World until its landing session
 * consumes it. A World entered without a session (nothing remembered), or one
 * whose recovery sends the route back to "/" without its search, opens it from
 * here. It is set only once the World is active (enterWorld switches it
 * synchronously), so a World that is no longer active owes nothing.
 */
export function owedWorldNotice(worldKey: string | null, now = Date.now()): string | null {
  if (owed === null) return null;
  if (
    useNeucleosWorldStore.getState().activeWorldKey !== owed.worldKey ||
    now - owed.at > OWED_NOTICE_WINDOW_MS
  ) {
    owed = null;
    return null;
  }
  return worldKey === owed.worldKey ? owed.notice : null;
}

/** The notice was opened (or abandoned): nothing is owed any more. */
export function settleWorldNotice(): void {
  owed = null;
}

/**
 * Opens a World view address (its decisions, or a contact or company) in the
 * session the person last stood in there, else the World's entry session.
 * Shared by the notification bell and the agent strip's Inbox, so both land
 * in the same place. Throws the reason it could not open.
 */
export async function openWorldNotice(
  worldKey: string,
  destination: NotificationDestination,
  navigate: ReturnType<typeof useNavigate>,
): Promise<void> {
  if (!useNeucleosWorldStore.getState().worlds.some((world) => world.worldKey === worldKey))
    throw new Error("This World is no longer available.");
  const notice =
    destination.id === undefined ? destination.kind : `${destination.kind}:${destination.id}`;
  const remembered = rememberedSessionOf(worldKey);
  const crossWorld = useNeucleosWorldStore.getState().activeWorldKey !== worldKey;
  // Review P2: only a World that remembers nothing lands on its entry
  // session; a remembered thread would restore that thread, never a session.
  if (readWorldRoom(worldKey) === "/" && crossWorld && worldEntryKeepsPage()) {
    // W-a67b0503: a World this Desk has nothing remembered for is entered as
    // the switcher enters it, landing on the World's own entry session (WS8),
    // and the landing session opens the owed notice. Reading the World's
    // whole room index first took 20-30 s.
    enterWorld(worldKey);
    owed = { worldKey, notice, at: Date.now() };
    return;
  }
  const sessionId = remembered ?? (await entrySessionFromIndex(worldKey));
  // Review P3: the World can leave while its room index is read.
  if (!useNeucleosWorldStore.getState().worlds.some((world) => world.worldKey === worldKey))
    throw new Error("This World is no longer available.");
  if (useNeucleosWorldStore.getState().activeWorldKey !== worldKey) {
    // SPA search navigation does not enter a World. Use the same entry
    // door as the switcher so browser and Electron both change authority.
    enterWorld(worldKey, { sessionId, notice });
    owed = { worldKey, notice, at: Date.now() };
  } else {
    owed = { worldKey, notice, at: Date.now() };
    await navigate({
      to: "/session/$sessionId",
      params: { sessionId },
      search: { world: worldKey, notice },
    });
  }
}

/**
 * W-a67b0503: the session the person last stood in opens at once, as the
 * World switcher opens it. Reading the World's whole room index first took
 * 20-30 s in a World of 120 sessions.
 */
function rememberedSessionOf(worldKey: string): string | null {
  const remembered = /^\/session\/([^/?]+)/.exec(readWorldRoom(worldKey))?.[1];
  if (remembered === undefined) return null;
  try {
    return decodeURIComponent(remembered);
  } catch {
    // A malformed remembered path is no session.
    return null;
  }
}

/** The World's entry session, from its room index: only where no faster door applies. */
async function entrySessionFromIndex(worldKey: string): Promise<string> {
  const readback = await readWorldRoomIndex(worldKey);
  if (!readback.ok) throw new Error(readback.reason);
  const available = readback.index.sessions.filter((row) => row.lifecycle !== "retired");
  const session = entrySessionOf({ ...readback.index, sessions: available });
  if (!session) throw new Error("This World has no available session. Open the World to continue.");
  return session.sessionId;
}
